import React, { useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Typography,
  Box,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ApiService from '../../services/api-service';
import notifier from '../Notifier';
import { getBalance } from '../../asyncAction/asyncBalance';
import { useDispatch, useSelector } from 'react-redux';
import { getErrorString, HMS, truncate } from '../../constants/function';
import { Images } from '../../assets/common';
import NoData from '../Nodata';
import { useTheme as useAppTheme } from '../../context/ThemeContext';

export default function SongRequestButton({ hostId, roomId, isOwner, open: controlledOpen, onClose: controlledOnClose }) {
  const { getCurrentBranding } = useAppTheme();
  const brandName = getCurrentBranding()?.appName || 'SIKI';
  const dispatch = useDispatch();
  const balance = useSelector((state) => state.balance.bal);
  const [internalOpen, setInternalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedSong, setSelectedSong] = useState(null);
  const [tipAmount, setTipAmount] = useState(10);
  const [loading, setLoading] = useState(false);
  const [showSearch, setShowSearch] = useState(false);

  // Use controlled or internal state
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = controlledOpen !== undefined ? () => {} : setInternalOpen;
  
  // Dispatch getBalance when dialog opens
  useEffect(() => {
    if (open) {
      dispatch(getBalance());
    }
  }, [open, dispatch]);

  console.log('[SongRequestButton] Props:', { hostId, roomId, isOwner });

  // Don't show button for hosts (unless controlled externally)
  if (isOwner && controlledOpen === undefined) {
    console.log('[SongRequestButton] Not showing - user is owner');
    return null;
  }
  
  // Don't show button if hostId or roomId is not available (unless controlled externally)
  if (!hostId || !roomId) {
    console.log('[SongRequestButton] Not showing - missing hostId or roomId', { hostId, roomId });
    if (controlledOpen === undefined) return null;
  }

  const handleOpen = () => {
    setOpen(true);
    dispatch(getBalance());
  };

  const handleClose = () => {
    if (controlledOnClose) {
      controlledOnClose();
    } else {
      setOpen(false);
    }
    setSelectedSong(null);
    setSearchQuery('');
    setSearchResults([]);
    setShowSearch(false);
    setTipAmount(10);
  };

  const handleSearch = async (query) => {
    const searchTerm = query || searchQuery;
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    try {
      const response = await ApiService.SEARCH(searchTerm);
      if (response && response.result && response.result.music) {
        setSearchResults(response.result.music);
      }
    } catch (error) {
      notifier.error('Failed to search songs');
    } finally {
      setSearching(false);
    }
  };

  // Debounced search effect
  useEffect(() => {
    if (!showSearch) return;
    
    const delayTimer = setTimeout(() => {
      if (searchQuery.trim()) {
        handleSearch(searchQuery);
      } else {
        setSearchResults([]);
      }
    }, 300); // 300ms delay

    return () => clearTimeout(delayTimer);
  }, [searchQuery, showSearch]);

  const handleSongSelect = (song) => {
    setSelectedSong(song);
    setShowSearch(false);
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleSubmit = async () => {
    if (!selectedSong) {
      notifier.error('Please select a song');
      return;
    }

    if (!hostId || !roomId) {
      notifier.error('Missing stream information. Please refresh and try again.');
      return;
    }

    if (tipAmount < 1) {
      notifier.error('Tip amount must be at least 1 SIKI');
      return;
    }

    if ((balance || 0) < tipAmount) {
      notifier.error('Insufficient balance');
      return;
    }

    setLoading(true);
    try {
      const requestData = {
        songId: selectedSong._id,
        hostId,
        tipAmount: Number(tipAmount),
        roomId
      };
      
      const response = await ApiService.SONG_REQUEST_SUBMIT(requestData);

      if (response.success) {
        notifier.success('Song request submitted successfully!');
        handleClose();
        dispatch(getBalance());
      }
    } catch (error) {
      notifier.error(getErrorString(error));
    } finally {
      setLoading(false);
    }
  };

  // If controlled externally, only render the dialog
  if (controlledOpen !== undefined) {
    return (
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: {
            backgroundColor: '#1A1A1A',
            color: '#ffffff',
            borderRadius: 16,
          },
        }}
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">Request a Song</Typography>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            {/* Song Selection */}
            <Box>
              <Typography variant="body2" sx={{ mb: 1, color: '#b0b0b0' }}>
                Select a song
              </Typography>
              {selectedSong && !showSearch ? (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#2A2A2A',
                    cursor: 'pointer',
                  }}
                  onClick={() => setShowSearch(true)}
                >
                  <img
                    src={selectedSong.thumbnailImg?.filePath || Images.default_music}
                    alt={selectedSong.title}
                    style={{
                      width: 50,
                      height: 50,
                      borderRadius: 8,
                      objectFit: 'cover',
                    }}
                  />
                  <Box flex={1}>
                    <Typography variant="body1">{truncate(selectedSong.title, 30)}</Typography>
                    <Typography variant="body2" sx={{ color: '#b0b0b0' }}>
                      {selectedSong.uploader?.firstName} {selectedSong.uploader?.lastName}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: '#b0b0b0' }}>
                    Change
                  </Typography>
                </Box>
              ) : (
                <Box>
                  {!showSearch ? (
                    <Button
                      onClick={() => setShowSearch(true)}
                      variant="outlined"
                      fullWidth
                      sx={{
                        py: 2,
                        borderColor: '#444',
                        color: '#ffffff',
                        '&:hover': {
                          borderColor: '#666',
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        },
                      }}
                    >
                      Click to search and select a song
                    </Button>
                  ) : (
                    <Box>
                      <TextField
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search for a song..."
                        fullWidth
                        autoFocus
                        InputProps={{
                          endAdornment: searching ? (
                            <InputAdornment position="end">
                              <CircularProgress size={20} />
                            </InputAdornment>
                          ) : searchQuery ? (
                            <InputAdornment position="end">
                              <IconButton onClick={() => setSearchQuery('')} size="small">
                                <CloseIcon />
                              </IconButton>
                            </InputAdornment>
                          ) : null,
                        }}
                        sx={{
                          mb: 2,
                          '& .MuiOutlinedInput-root': {
                            color: '#ffffff',
                            '& fieldset': {
                              borderColor: '#444',
                            },
                            '&:hover fieldset': {
                              borderColor: '#666',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#4CAF50',
                            },
                          },
                        }}
                      />
                      <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                        {searchResults.length > 0 ? (
                          <List>
                            {searchResults.map((song) => (
                              <ListItem
                                key={song._id}
                                onClick={() => handleSongSelect(song)}
                                sx={{
                                  '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                  },
                                }}
                              >
                                <ListItemAvatar>
                                  <Avatar
                                    src={song.thumbnailImg?.filePath || Images.default_music}
                                    variant="rounded"
                                  />
                                </ListItemAvatar>
                                <ListItemText
                                  primary={truncate(song.title, 30)}
                                  secondary={
                                    <Typography variant="body2" sx={{ color: '#b0b0b0' }}>
                                      {song.uploader?.firstName} {song.uploader?.lastName} • {HMS(song.duration)}
                                    </Typography>
                                  }
                                />
                              </ListItem>
                            ))}
                          </List>
                        ) : searchQuery && !searching ? (
                          <NoData label="No songs found" />
                        ) : null}
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
            </Box>

            {/* Tip Amount */}
            <Box>
              <Typography variant="body2" sx={{ mb: 1, color: '#b0b0b0' }}>
                Tip amount
              </Typography>
              <TextField
                type="number"
                value={tipAmount}
                onChange={(e) => setTipAmount(Math.max(1, parseInt(e.target.value) || 1))}
                fullWidth
                InputProps={{
                  endAdornment: <InputAdornment position="end">{brandName}</InputAdornment>,
                  inputProps: { min: 1 },
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#ffffff',
                    '& fieldset': {
                      borderColor: '#444',
                    },
                    '&:hover fieldset': {
                      borderColor: '#666',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#4CAF50',
                    },
                  },
                }}
              />
              <Typography variant="caption" sx={{ mt: 1, display: 'block', color: '#b0b0b0' }}>
                Higher tips increase the chance your song will be played sooner
              </Typography>
            </Box>

            {/* Balance Display */}
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                backgroundColor: '#2A2A2A',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant="body2" sx={{ color: '#b0b0b0' }}>
                Your balance:
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {(balance || 0).toFixed(2)} {brandName}
              </Typography>
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleClose} sx={{ color: '#b0b0b0' }}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || !selectedSong || tipAmount < 1 || (balance || 0) < tipAmount}
            sx={{
              backgroundColor: '#4CAF50',
              '&:hover': {
                backgroundColor: '#45a049',
              },
              '&.Mui-disabled': {
                backgroundColor: '#333',
                color: '#666',
              },
            }}
          >
            {loading ? 'Submitting...' : `Request for ${tipAmount} ${brandName}`}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // Otherwise render button + dialog
  return (
    <>
      <Button
        onClick={handleOpen}
        variant="contained"
        startIcon={
          <img 
            src={Images.jukebox} 
            alt="Request Song" 
            style={{ width: 20, height: 20, filter: 'brightness(0) saturate(100%) invert(100%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(100%) contrast(100%)' }}
          />
        }
        sx={{
          backgroundColor: '#4CAF50',
          color: '#ffffff',
          textTransform: 'none',
          '&:hover': {
            backgroundColor: '#45a049',
          },
          padding: '8px 16px',
          fontSize: '14px',
          fontWeight: 500,
        }}
      >
        Request Song
      </Button>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: {
            backgroundColor: '#1A1A1A',
            color: '#ffffff',
            borderRadius: 16,
          },
        }}
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">Request a Song</Typography>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            {/* Song Selection */}
            <Box>
              <Typography variant="body2" sx={{ mb: 1, color: '#b0b0b0' }}>
                Select a song
              </Typography>
              {selectedSong && !showSearch ? (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#2A2A2A',
                    cursor: 'pointer',
                  }}
                  onClick={() => setShowSearch(true)}
                >
                  <img
                    src={selectedSong.thumbnailImg?.filePath || Images.default_music}
                    alt={selectedSong.title}
                    style={{
                      width: 50,
                      height: 50,
                      borderRadius: 8,
                      objectFit: 'cover',
                    }}
                  />
                  <Box flex={1}>
                    <Typography variant="body1">{truncate(selectedSong.title, 30)}</Typography>
                    <Typography variant="body2" sx={{ color: '#b0b0b0' }}>
                      {selectedSong.uploader?.firstName} {selectedSong.uploader?.lastName}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: '#b0b0b0' }}>
                    Change
                  </Typography>
                </Box>
              ) : (
                <Box>
                  {!showSearch ? (
                    <Button
                      onClick={() => setShowSearch(true)}
                      variant="outlined"
                      fullWidth
                      sx={{
                        py: 2,
                        borderColor: '#444',
                        color: '#ffffff',
                        '&:hover': {
                          borderColor: '#666',
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        },
                      }}
                    >
                      Click to search and select a song
                    </Button>
                  ) : (
                    <Box>
                      <TextField
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search for a song..."
                        fullWidth
                        autoFocus
                        InputProps={{
                          endAdornment: searching ? (
                            <InputAdornment position="end">
                              <CircularProgress size={20} />
                            </InputAdornment>
                          ) : searchQuery ? (
                            <InputAdornment position="end">
                              <IconButton onClick={() => setSearchQuery('')} size="small">
                                <CloseIcon />
                              </IconButton>
                            </InputAdornment>
                          ) : null,
                        }}
                        sx={{
                          mb: 2,
                          '& .MuiOutlinedInput-root': {
                            color: '#ffffff',
                            '& fieldset': {
                              borderColor: '#444',
                            },
                            '&:hover fieldset': {
                              borderColor: '#666',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#4CAF50',
                            },
                          },
                        }}
                      />
                      <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                        {searchResults.length > 0 ? (
                          <List>
                            {searchResults.map((song) => (
                              <ListItem
                                key={song._id}
                                onClick={() => handleSongSelect(song)}
                                sx={{
                                  '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                  },
                                }}
                              >
                                <ListItemAvatar>
                                  <Avatar
                                    src={song.thumbnailImg?.filePath || Images.default_music}
                                    variant="rounded"
                                  />
                                </ListItemAvatar>
                                <ListItemText
                                  primary={truncate(song.title, 30)}
                                  secondary={
                                    <Typography variant="body2" sx={{ color: '#b0b0b0' }}>
                                      {song.uploader?.firstName} {song.uploader?.lastName} • {HMS(song.duration)}
                                    </Typography>
                                  }
                                />
                              </ListItem>
                            ))}
                          </List>
                        ) : searchQuery && !searching ? (
                          <NoData label="No songs found" />
                        ) : null}
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
            </Box>

            {/* Tip Amount */}
            <Box>
              <Typography variant="body2" sx={{ mb: 1, color: '#b0b0b0' }}>
                Tip amount
              </Typography>
              <TextField
                type="number"
                value={tipAmount}
                onChange={(e) => setTipAmount(Math.max(1, parseInt(e.target.value) || 1))}
                fullWidth
                InputProps={{
                  endAdornment: <InputAdornment position="end">{brandName}</InputAdornment>,
                  inputProps: { min: 1 },
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#ffffff',
                    '& fieldset': {
                      borderColor: '#444',
                    },
                    '&:hover fieldset': {
                      borderColor: '#666',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#4CAF50',
                    },
                  },
                }}
              />
              <Typography variant="caption" sx={{ mt: 1, display: 'block', color: '#b0b0b0' }}>
                Higher tips increase the chance your song will be played sooner
              </Typography>
            </Box>

            {/* Balance Display */}
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                backgroundColor: '#2A2A2A',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant="body2" sx={{ color: '#b0b0b0' }}>
                Your balance:
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {(balance || 0).toFixed(2)} {brandName}
              </Typography>
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleClose} sx={{ color: '#b0b0b0' }}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || !selectedSong || tipAmount < 1 || (balance || 0) < tipAmount}
            sx={{
              backgroundColor: '#4CAF50',
              '&:hover': {
                backgroundColor: '#45a049',
              },
              '&.Mui-disabled': {
                backgroundColor: '#333',
                color: '#666',
              },
            }}
          >
            {loading ? 'Submitting...' : `Request for ${tipAmount} ${brandName}`}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
